// quantity-moq.js
// Fixed version.
//
// WHAT WAS WRONG BEFORE:
// 1. `document.querySelectorAll('.quantity-input')` was selecting elements by
//    CLASS name (".quantity-input"), but nothing in the theme markup carries
//    that class — the wrapper is a custom element with the TAG name
//    `quantity-input` (e.g. <quantity-input class="quantity ...">).
//    Because of the leading dot, this selector matched nothing, so this
//    entire block never ran on any page.
// 2. Even if #1 were fixed, the "minus" button handler referenced a variable
//    `currentValue` that was never declared/read in that scope (the "plus"
//    handler declared it locally with `let`, the "minus" handler did not).
//    That is a ReferenceError waiting to happen.
// 3. The plus/minus buttons in the collection grid, search results, and cart
//    line items ALSO carry Shopify Dawn's native classes
//    (`quantity__button`, `quantity__input`) and sit inside a
//    `<quantity-input>` custom element. Dawn's own component
//    (assets/global.js) automatically binds a click handler to *every*
//    <button> inside that element. So if this script's own click handlers
//    were also attached (once #1 is fixed), every click on "+"/"-" would
//    fire BOTH handlers and increment/decrement the field twice per click —
//    this is what produces the erratic / inconsistent quantity counting.
// 4. The tooltip logic at the bottom used `document.querySelector('.moq-input')`
//    and `document.getElementById('tooltip-1')` — i.e. it only ever looked at
//    the FIRST matching element on the whole page. On a collection grid with
//    many product cards (many `.moq-input` fields, and likely a duplicated
//    `id="tooltip-1"` per card, which is itself invalid HTML), this meant the
//    tooltip only ever worked for one arbitrary card and could react to the
//    wrong product entirely.
//
// FIX APPROACH:
// Dawn's native <quantity-input> component already respects the input's
// `min`, `max`, and `step` HTML attributes — and those are already being set
// correctly from the MOQ / quantity_rule values in Liquid
// (min="{{ moq }}", step="{{ quantity_rule.increment }}", etc). So instead of
// re-implementing +/- stepping a second time (and fighting Dawn's handler),
// this script now ONLY does the things Dawn's component does not do:
//   - shows/hides the "minimum quantity" tooltip, per card
//   - snaps a manually-typed value back to the MOQ if it's invalid
// It listens for the native `change`/`input` events on each `.moq-input`
// field individually (scoped with closest/querySelector per wrapper) instead
// of adding duplicate click handlers on the buttons.

document.addEventListener('DOMContentLoaded', () => {
  // Scope to each quantity wrapper on the page. `quantity-input` is a tag
  // name (custom element), not a class — do NOT prefix it with a dot.
  document.querySelectorAll('quantity-input').forEach((wrapper) => {
    const inputField = wrapper.querySelector('.moq-input');
    if (!inputField) return; // not an MOQ-enabled field, nothing to do here

    const minusButton = wrapper.querySelector('.moq-minus');
    const moq = parseInt(inputField.dataset.min ?? inputField.min, 10) || 1;

    // Look for a tooltip that belongs to THIS card only. The tooltip markup
    // (e.g. in main-cart-items.liquid) currently sits as a SIBLING of
    // <quantity-input>, not inside it, and reuses the same id="tooltip-1" for
    // every cart line item — which is invalid HTML and means
    // document.getElementById('tooltip-1') always finds the same one
    // regardless of which line item you're editing. Replace that id with
    // e.g. data-moq-tooltip="{{ item.index }}" in the Liquid and this will
    // find the right one automatically. Falls back gracefully if no tooltip
    // markup exists for a given card (e.g. collection grid, search results).
    const scope = wrapper.parentElement || wrapper;
    const tooltip =
      wrapper.querySelector('[data-moq-tooltip]') ||
      scope.querySelector('[data-moq-tooltip]') ||
      scope.querySelector('.custom-tooltip');

    function updateMinusButtonState() {
      if (!minusButton) return;
      const value = parseInt(inputField.value, 10);
      minusButton.disabled = !isNaN(value) && value <= moq;
    }

    function showTooltip(message) {
      if (!tooltip) return;
      tooltip.textContent = message;
      tooltip.classList.add('show');
    }

    function hideTooltipSoon() {
      if (!tooltip) return;
      setTimeout(() => tooltip.classList.remove('show'), 1500);
    }

    function checkInput() {
      const value = parseInt(inputField.value, 10);
      if (isNaN(value) || value < moq) {
        showTooltip(`This item has a minimum of ${moq}.`);
      } else if (tooltip) {
        tooltip.classList.remove('show');
      }
    }

    // Snap back to MOQ if the user types (or the field ends up with) an
    // invalid value. This intentionally does NOT touch the +/- buttons —
    // Dawn's native quantity-input component already handles those via the
    // input's min/max/step attributes, so we don't double-bind them here.
    function handleManualInputReversion() {
      const value = parseInt(inputField.value, 10);
      if (isNaN(value) || value < moq) {
        inputField.value = moq;
        inputField.dispatchEvent(new Event('change', { bubbles: true }));
      }
      updateMinusButtonState();
    }

    inputField.addEventListener('input', () => {
      checkInput();
      updateMinusButtonState();
    });

    inputField.addEventListener('change', handleManualInputReversion);
    inputField.addEventListener('blur', hideTooltipSoon);

    // Initial state on load.
    updateMinusButtonState();
    checkInput();
  });
});
